package es.cragma.fichajefacil.ui.screens.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import es.cragma.fichajefacil.data.AppRepository
import es.cragma.fichajefacil.model.User
import es.cragma.fichajefacil.model.UserRole
import es.cragma.fichajefacil.ui.components.CragmaBrandLogo
import es.cragma.fichajefacil.ui.components.RoleBadge
import es.cragma.fichajefacil.ui.components.StyledButtonPrimary
import es.cragma.fichajefacil.ui.components.StyledButtonWhite
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.theme.*

@Composable
fun ManagementDashboardScreen(
    currentUser: User,
    repository: AppRepository,
    currentLanguage: AppLanguage = AppLanguage.SPANISH,
    onSwitchToWorkerMode: () -> Unit,
    onNavigateToRegisterCompany: () -> Unit,
    onLogout: () -> Unit,
    onSwitchCompany: (() -> Unit)? = null
) {
    val s = remember(currentLanguage) { LocalizedStrings.get(currentLanguage) }
    var selectedMenu by remember { mutableStateOf(1) } // Default to Fichajes
    var isMobileDrawerOpen by remember { mutableStateOf(false) }
    val company = currentUser.companyId?.let { repository.getCompany(it) }

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF0F172A)) // Cragma Dark Navy
            .navigationBarsPadding()
    ) {
        val isMobile = maxWidth < 700.dp

        if (isMobile) {
            // MOBILE RESPONSIVE LAYOUT
            Box(modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.fillMaxSize()) {
                    // Mobile Header
                    Surface(
                        color = Color(0xFF1E293B),
                        shadowElevation = 4.dp
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .windowInsetsPadding(WindowInsets.statusBars)
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 12.dp, vertical = 10.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    IconButton(onClick = { isMobileDrawerOpen = true }) {
                                        Text("☰", fontSize = 22.sp, color = Color.White)
                                    }
                                    Spacer(modifier = Modifier.width(4.dp))
                                    Column {
                                        Text(
                                            text = currentUser.name,
                                            fontWeight = FontWeight.Bold,
                                            color = Color.White,
                                            fontSize = 14.sp
                                        )
                                        Text(
                                            text = company?.name ?: "Administración",
                                            fontSize = 11.sp,
                                            color = Color(0xFF94A3B8)
                                        )
                                    }
                                }

                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Button(
                                        onClick = onSwitchToWorkerMode,
                                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0284C7)),
                                        shape = RoundedCornerShape(8.dp),
                                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                                        modifier = Modifier.padding(end = 4.dp)
                                    ) {
                                        Text("📱 Trabajador", fontSize = 11.sp, fontWeight = FontWeight.Bold)
                                    }

                                    IconButton(onClick = onLogout) {
                                        Text("🚪", fontSize = 18.sp)
                                    }
                                }
                            }

                            // Mobile Tab Strip for fast navigation
                            ScrollableTabRow(
                                selectedTabIndex = when (selectedMenu) {
                                    6 -> 0
                                    1 -> 1
                                    7 -> 2
                                    else -> 1
                                },
                                containerColor = Color(0xFF1E293B),
                                contentColor = Color.White,
                                edgePadding = 12.dp
                            ) {
                                Tab(
                                    selected = selectedMenu == 6,
                                    onClick = { selectedMenu = 6 },
                                    text = { Text("👥 Usuarios", fontSize = 12.sp, fontWeight = FontWeight.Bold) }
                                )
                                Tab(
                                    selected = selectedMenu == 1,
                                    onClick = { selectedMenu = 1 },
                                    text = { Text("⏱ Fichajes", fontSize = 12.sp, fontWeight = FontWeight.Bold) }
                                )
                                Tab(
                                    selected = selectedMenu == 7,
                                    onClick = { selectedMenu = 7 },
                                    text = { Text("🏢 Empresa", fontSize = 12.sp, fontWeight = FontWeight.Bold) }
                                )
                            }
                        }
                    }

                    // Mobile Main Body Panel
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(8.dp)
                    ) {
                        when (selectedMenu) {
                            0, 1, 2, 8 -> ClockInRecordsScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                            6 -> EmployeesManagementScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                            7 -> CompanySettingsScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                            else -> ClockInRecordsScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                        }
                    }
                }

                // Mobile Navigation Drawer Overlay
                if (isMobileDrawerOpen) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(Color.Black.copy(alpha = 0.6f))
                            .clickable { isMobileDrawerOpen = false }
                    ) {
                        Surface(
                            modifier = Modifier
                                .fillMaxHeight()
                                .width(280.dp)
                                .clickable(enabled = false) {},
                            color = Color(0xFF1E293B),
                            shadowElevation = 16.dp
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(16.dp)
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    CragmaBrandLogo(modifier = Modifier.height(20.dp))
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text("Ficha Fácil Admin", fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                }

                                Spacer(modifier = Modifier.height(16.dp))

                                Column(
                                    modifier = Modifier
                                        .weight(1f)
                                        .verticalScroll(rememberScrollState())
                                ) {
                                    SidebarMenuItem(icon = "👥", title = "Usuarios y Empleados", isSelected = selectedMenu == 6) {
                                        selectedMenu = 6
                                        isMobileDrawerOpen = false
                                    }

                                    SidebarMenuItem(icon = "⏱", title = "Fichajes y Registros", isSelected = selectedMenu == 1) {
                                        selectedMenu = 1
                                        isMobileDrawerOpen = false
                                    }

                                    SidebarMenuItem(icon = "🏢", title = "Configuración Empresa", isSelected = selectedMenu == 7) {
                                        selectedMenu = 7
                                        isMobileDrawerOpen = false
                                    }
                                }

                                Spacer(modifier = Modifier.height(16.dp))

                                if (repository.getUsersByEmail(currentUser.email).size > 1 && onSwitchCompany != null) {
                                    StyledButtonWhite(
                                        text = "🏢 Cambiar de Empresa",
                                        onClick = {
                                            isMobileDrawerOpen = false
                                            onSwitchCompany()
                                        }
                                    )
                                    Spacer(modifier = Modifier.height(8.dp))
                                }

                                StyledButtonPrimary(
                                    text = "📱 Ir a Modo Trabajador",
                                    onClick = {
                                        isMobileDrawerOpen = false
                                        onSwitchToWorkerMode()
                                    }
                                )

                                Spacer(modifier = Modifier.height(8.dp))

                                Button(
                                    onClick = {
                                        isMobileDrawerOpen = false
                                        onLogout()
                                    },
                                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                                    shape = RoundedCornerShape(12.dp),
                                    modifier = Modifier.fillMaxWidth().height(42.dp)
                                ) {
                                    Text("🚪 Cerrar Sesión", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                }
                            }
                        }
                    }
                }
            }
        } else {
            // DESKTOP / WEB LAYOUT (With Left Sidebar)
            Row(modifier = Modifier.fillMaxSize()) {
                // 1. LEFT NAVIGATION SIDEBAR
                Surface(
                    color = Color(0xFF1E293B),
                    shadowElevation = 8.dp,
                    modifier = Modifier
                        .width(260.dp)
                        .fillMaxHeight()
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(16.dp)
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            CragmaBrandLogo(modifier = Modifier.height(22.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = "Ficha Fácil Web",
                                fontSize = 15.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )
                        }
                        Spacer(modifier = Modifier.height(2.dp))
                        Text(
                            text = company?.name ?: "Administración Global",
                            fontSize = 11.sp,
                            color = Color(0xFF94A3B8)
                        )

                        Spacer(modifier = Modifier.height(20.dp))

                        Column(
                            modifier = Modifier
                                .weight(1f)
                                .verticalScroll(rememberScrollState())
                        ) {
                            Text("PRINCIPAL", fontSize = 10.sp, fontWeight = FontWeight.ExtraBold, color = Color(0xFF64748B))
                            Spacer(modifier = Modifier.height(6.dp))

                            SidebarMenuItem(icon = "🏠", title = "Inicio", isSelected = selectedMenu == 0) { selectedMenu = 0 }
                            SidebarMenuItem(icon = "⏱", title = "Fichajes", isSelected = selectedMenu == 1) { selectedMenu = 1 }
                            SidebarMenuItem(icon = "⚖️", title = "Balance de horas", isSelected = selectedMenu == 2) { selectedMenu = 2 }
                            SidebarMenuItem(icon = "📄", title = "Documentos", isSelected = selectedMenu == 3) { selectedMenu = 3 }
                            SidebarMenuItem(icon = "🏖", title = "Ausencias y vacaciones", isSelected = selectedMenu == 4) { selectedMenu = 4 }

                            Spacer(modifier = Modifier.height(16.dp))
                            Text("MI ÁREA", fontSize = 10.sp, fontWeight = FontWeight.ExtraBold, color = Color(0xFF64748B))
                            Spacer(modifier = Modifier.height(6.dp))

                            SidebarMenuItem(icon = "🔔", title = "Alertas (3)", isSelected = selectedMenu == 5) { selectedMenu = 5 }

                            if (currentUser.role.isManagement) {
                                Spacer(modifier = Modifier.height(16.dp))
                                Text("GESTIÓN", fontSize = 10.sp, fontWeight = FontWeight.ExtraBold, color = Color(0xFF64748B))
                                Spacer(modifier = Modifier.height(6.dp))

                                SidebarMenuItem(icon = "👥", title = "Usuarios", isSelected = selectedMenu == 6) { selectedMenu = 6 }
                                SidebarMenuItem(icon = "🏢", title = "Empresa", isSelected = selectedMenu == 7) { selectedMenu = 7 }
                                SidebarMenuItem(icon = "📈", title = "Informes", isSelected = selectedMenu == 8) { selectedMenu = 8 }
                                SidebarMenuItem(icon = "📅", title = "Cuadrante", isSelected = selectedMenu == 9) { selectedMenu = 9 }
                            }
                        }

                        // Footer Profile Box
                        Surface(
                            color = Color(0xFF334155),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Surface(
                                        color = Color(0xFF0284C7),
                                        shape = CircleShape,
                                        modifier = Modifier.size(32.dp)
                                    ) {
                                        Box(contentAlignment = Alignment.Center) {
                                            Text(
                                                text = currentUser.firstName.take(1),
                                                color = Color.White,
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 14.sp
                                            )
                                        }
                                    }
                                    Spacer(modifier = Modifier.width(10.dp))
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(
                                            text = currentUser.name,
                                            fontSize = 12.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = Color.White
                                        )
                                        Text(
                                            text = currentUser.email,
                                            fontSize = 10.sp,
                                            color = Color(0xFF94A3B8)
                                        )
                                    }
                                }

                                Spacer(modifier = Modifier.height(10.dp))

                                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                    Button(
                                        onClick = onSwitchToWorkerMode,
                                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0284C7)),
                                        shape = RoundedCornerShape(8.dp),
                                        modifier = Modifier.weight(1f).height(32.dp),
                                        contentPadding = PaddingValues(0.dp)
                                    ) {
                                        Text("📱 Trabajador", fontSize = 10.sp, fontWeight = FontWeight.Bold)
                                    }

                                    Button(
                                        onClick = onLogout,
                                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                                        shape = RoundedCornerShape(8.dp),
                                        modifier = Modifier.weight(1f).height(32.dp),
                                        contentPadding = PaddingValues(0.dp)
                                    ) {
                                        Text("🚪 Salir", fontSize = 10.sp, fontWeight = FontWeight.Bold)
                                    }
                                }
                            }
                        }
                    }
                }

                // 2. MAIN CONTENT PANEL
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .background(Color(0xFF0F172A))
                ) {
                    Surface(
                        color = Color(0xFF1E293B),
                        shadowElevation = 2.dp
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .windowInsetsPadding(WindowInsets.statusBars)
                                .padding(horizontal = 20.dp, vertical = 12.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = "Inicio > ${getMenuTitle(selectedMenu)}",
                                fontSize = 13.sp,
                                color = Color(0xFF94A3B8)
                            )

                            Row(verticalAlignment = Alignment.CenterVertically) {
                                if (repository.getUsersByEmail(currentUser.email).size > 1 && onSwitchCompany != null) {
                                    Button(
                                        onClick = onSwitchCompany,
                                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF334155)),
                                        shape = RoundedCornerShape(8.dp),
                                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                                        modifier = Modifier.padding(end = 10.dp)
                                    ) {
                                        Text("🏢 Cambiar Empresa", fontSize = 11.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                    }
                                }

                                RoleBadge(role = currentUser.role, currentLanguage = currentLanguage)
                            }
                        }
                    }

                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(16.dp)
                    ) {
                        when (selectedMenu) {
                            0, 1, 2, 8 -> ClockInRecordsScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                            6 -> EmployeesManagementScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                            7 -> CompanySettingsScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                            else -> ClockInRecordsScreen(currentUser = currentUser, repository = repository, currentLanguage = currentLanguage)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SidebarMenuItem(
    icon: String,
    title: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Surface(
        onClick = onClick,
        color = if (isSelected) Color(0xFF0284C7) else Color.Transparent,
        shape = RoundedCornerShape(10.dp),
        modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(icon, fontSize = 16.sp)
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = title,
                fontSize = 13.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) Color.White else Color(0xFFCBD5E1)
            )
        }
    }
}

private fun getMenuTitle(menuIndex: Int): String {
    return when (menuIndex) {
        0 -> "Inicio / Dashboard"
        1 -> "Fichajes"
        2 -> "Balance de Horas"
        3 -> "Documentos"
        4 -> "Ausencias y Vacaciones"
        5 -> "Alertas"
        6 -> "Usuarios"
        7 -> "Empresa"
        8 -> "Informes"
        9 -> "Cuadrante"
        else -> "Gestión"
    }
}
